import { Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { F } from "@/components/ajustes/Field";

const CHANNELS = ["telegram", "whatsapp"];

const ChannelSwitches = ({ s, sec, field, testidPrefix }) => (
  <div className="flex flex-wrap gap-5 mb-3">
    {CHANNELS.map((ch) => (
      <label key={ch} className="flex items-center gap-2 text-sm capitalize">
        <Switch
          checked={(s.digest?.[field] || []).includes(ch)}
          onCheckedChange={(v) => sec("digest", field,
            v ? [...(s.digest?.[field] || []), ch]
              : (s.digest?.[field] || []).filter((x) => x !== ch))}
          data-testid={`${testidPrefix}-${ch}-switch`}
        />
        {ch}
      </label>
    ))}
  </div>
);

export const DigestPanel = ({ s, sec, busy, runs, runNow, sendSummaryNow, children }) => (
  <div className="border-t border-border pt-4">
    <div className="kicker mb-2">Envío automático del boletín</div>
    <p className="text-xs text-muted-foreground mb-3">
      A cada hora indicada la app genera el boletín de cada medio con las noticias publicadas
      desde el envío anterior, le añade los anuncios activos y lo envía a los suscriptores,
      sin intervención.
    </p>
    <F label="Horas de envío (coma)" hint="Formato 24 horas · zona horaria America/Santo_Domingo">
      <Input value={(s.digest?.times || []).join(", ")}
        onChange={(e) => sec("digest", "times", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))}
        data-testid="digest-times-input" />
    </F>
    <div className="flex items-center gap-3">
      <Switch checked={!!s.digest?.enabled} onCheckedChange={(v) => sec("digest", "enabled", v)} data-testid="digest-enabled-switch" />
      <span className="text-sm">Envío automático habilitado</span>
    </div>
    <div className="mt-4">
      <div className="kicker mb-2">Enviar también el boletín resumido por mensajería</div>
      <div className="flex flex-wrap gap-4">
        {CHANNELS.map((ch) => (
          <label key={ch} className="flex items-center gap-2 text-sm cursor-pointer">
            <Switch
              checked={(s.digest?.messaging_channels || []).includes(ch)}
              onCheckedChange={(v) => sec("digest", "messaging_channels",
                v ? [...(s.digest?.messaging_channels || []), ch]
                  : (s.digest?.messaging_channels || []).filter((x) => x !== ch))}
              data-testid={`digest-messaging-${ch}-switch`}
            />
            <span className="capitalize">{ch}</span>
          </label>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-2">
        Usa el chat de Telegram y los destinatarios de WhatsApp configurados en cada medio.
      </p>
    </div>

    <div className="mt-5 border-t border-border pt-4">
      <div className="kicker mb-2">Resumen matutino por WhatsApp y Telegram</div>
      <p className="text-xs text-muted-foreground mb-3">
        A la hora indicada envía, por medio, los titulares publicados con su enlace y la
        invitación a los canales. Funciona aunque el boletín por correo esté apagado.
      </p>
      <F label="Horas del resumen (coma)" hint="Por ejemplo 07:00 o 07:00, 18:00">
        <Input value={(s.digest?.messaging_summary_times || []).join(", ")}
          onChange={(e) => sec("digest", "messaging_summary_times",
            e.target.value.split(",").map((x) => x.trim()).filter(Boolean))}
          data-testid="summary-times-input" />
      </F>
      <F label="Ventana de noticias (horas)" hint="Cuántas horas hacia atrás se incluyen">
        <Input type="number" min="1" max="72" value={s.digest?.messaging_summary_hours ?? 24}
          onChange={(e) => sec("digest", "messaging_summary_hours", Number(e.target.value))}
          data-testid="summary-hours-input" />
      </F>
      <div className="flex items-center gap-3">
        <Switch checked={!!s.digest?.messaging_summary_enabled}
          onCheckedChange={(v) => sec("digest", "messaging_summary_enabled", v)}
          data-testid="summary-enabled-switch" />
        <span className="text-sm">Resumen automático habilitado</span>
      </div>
      <Button variant="outline" className="mt-3" onClick={sendSummaryNow}
        disabled={busy === "summary"} data-testid="run-summary-now-btn">
        <Play className="h-4 w-4 mr-2" />
        {busy === "summary" ? "Enviando…" : "Enviar resumen ahora"}
      </Button>
    </div>

    {children}

    <div className="mt-5 border-t border-border pt-4">
      <div className="kicker mb-2">Boletín por medio</div>
      <p className="text-xs text-muted-foreground mb-3">
        Cada medio envía su propio boletín, con su logo y sus espacios de anuncio, a su
        base de suscriptores. Active la opción para no incluir a los suscriptores generales.
      </p>
      <div className="flex items-center gap-3">
        <Switch checked={!!s.newsletter?.strict_outlet_subscribers}
          onCheckedChange={(v) => sec("newsletter", "strict_outlet_subscribers", v)}
          data-testid="strict-subscribers-switch" />
        <span className="text-sm">Enviar solo a los suscriptores de cada medio</span>
      </div>
    </div>

    <div className="flex flex-wrap gap-2 mt-4">
      <Button variant="outline" onClick={runNow} disabled={busy === "run"} data-testid="run-digest-now-btn">
        <Play className="h-4 w-4 mr-2" />{busy === "run" ? "Enviando…" : "Ejecutar ahora"}
      </Button>
    </div>
    {(runs || []).length > 0 && (
      <div className="mt-5" data-testid="digest-runs">
        <div className="kicker mb-2">Últimos envíos automáticos</div>
        <ul className="text-xs space-y-2">
          {runs.slice(0, 6).map((r) => (
            <li key={r.id} className="border-b border-border/60 pb-2">
              <span className="mono">{r.id}</span>{r.manual ? " · manual" : ""}
              <ul className="text-muted-foreground mt-1 space-y-0.5">
                {(r.results || []).map((x) => (
                  <li key={`${r.id}-${x.outlet}`}>
                    {x.outlet}: {x.error ? `error — ${x.error}` : x.reason ? x.reason : `${x.sent}/${x.recipients} enviados`}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

export const ReportsPanel = ({ s, sec, busy, report, weekly, weeklyPreview,
                               sendReportNow, sendWeeklyNow, previewWeekly }) => (
  <>
    <div className="mt-5 border-t border-border pt-4">
      <div className="kicker mb-2">Parte diario de la redacción</div>
      <p className="text-xs text-muted-foreground mb-3">
        Mensaje interno con lo que entró, se redactó, se publicó y se archivó, más las
        notas pendientes más antiguas. Va a su chat de Telegram y a WhatsApp si lo activa.
      </p>
      <F label="Horas del parte (coma)" hint="Por ejemplo 05:30">
        <Input value={(s.digest?.newsroom_report_times || []).join(", ")}
          onChange={(e) => sec("digest", "newsroom_report_times",
            e.target.value.split(",").map((x) => x.trim()).filter(Boolean))}
          data-testid="report-times-input" />
      </F>
      <F label="Ventana del parte (horas)">
        <Input type="number" min="1" max="72" value={s.digest?.newsroom_report_hours ?? 24}
          onChange={(e) => sec("digest", "newsroom_report_hours", Number(e.target.value))}
          data-testid="report-hours-input" />
      </F>
      <F label="Chat privado de Telegram para el parte"
         hint="Su ID numérico o @usuario. No se usa el canal público para no publicarlo. Obtenga el ID escribiendo a @userinfobot.">
        <Input value={s.digest?.newsroom_report_chat_id || ""}
          onChange={(e) => sec("digest", "newsroom_report_chat_id", e.target.value)}
          data-testid="report-chat-id-input" />
      </F>
      <F label="WhatsApp para el parte (números con código, separados por coma)">
        <Input value={(s.digest?.newsroom_report_whatsapp || []).join(", ")}
          onChange={(e) => sec("digest", "newsroom_report_whatsapp",
            e.target.value.split(",").map((x) => x.trim()).filter(Boolean))}
          data-testid="report-whatsapp-input" />
      </F>
      <F label="Pendientes a listar">
        <Input type="number" min="0" max="20" value={s.digest?.newsroom_report_pending ?? 5}
          onChange={(e) => sec("digest", "newsroom_report_pending", Number(e.target.value))}
          data-testid="report-pending-input" />
      </F>
      <ChannelSwitches s={s} sec={sec} field="newsroom_report_channels" testidPrefix="report" />
      <div className="flex items-center gap-3">
        <Switch checked={s.digest?.newsroom_report_by_outlet !== false}
          onCheckedChange={(v) => sec("digest", "newsroom_report_by_outlet", v)}
          data-testid="report-by-outlet-switch" />
        <span className="text-sm">Separar el parte por medio</span>
      </div>
      <div className="flex items-center gap-3 mt-3">
        <Switch checked={!!s.digest?.newsroom_report_enabled}
          onCheckedChange={(v) => sec("digest", "newsroom_report_enabled", v)}
          data-testid="report-enabled-switch" />
        <span className="text-sm">Parte automático habilitado</span>
      </div>
      <Button variant="outline" className="mt-3" onClick={sendReportNow}
        disabled={busy === "report"} data-testid="run-report-now-btn">
        <Play className="h-4 w-4 mr-2" />
        {busy === "report" ? "Enviando…" : "Enviar parte ahora"}
      </Button>
      {report && (
        <p className="text-xs text-muted-foreground mt-2" data-testid="report-result">
          {report.ran
            ? `Enviado por ${(report.channels || []).join(", ") || "ningún canal"} · ${report.counts?.publicadas ?? 0} publicadas, ${report.counts?.en_revision ?? 0} en revisión`
            : `Sin enviar: ${report.reason}`}
          {(report.errors || []).length > 0 && ` · ${report.errors[0].error}`}
        </p>
      )}
    </div>

    <div className="mt-5 border-t border-border pt-4">
      <div className="kicker mb-2">Parte semanal de la redacción</div>
      <p className="text-xs text-muted-foreground mb-3">
        Los lunes a primera hora: totales de la semana cerrada, el medio que más publicó,
        el día de mayor producción y las notas destacadas. Usa el mismo chat privado de
        Telegram del parte diario.
      </p>
      <F label="Día de envío">
        <select className="w-full h-10 border border-input bg-background px-3 text-sm rounded-md"
          value={s.digest?.weekly_report_day ?? 0}
          onChange={(e) => sec("digest", "weekly_report_day", Number(e.target.value))}
          data-testid="weekly-day-select">
          {["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"]
            .map((d, i) => <option key={d} value={i}>{d}</option>)}
        </select>
      </F>
      <F label="Hora del parte semanal" hint="Por ejemplo 05:30">
        <Input value={(s.digest?.weekly_report_times || []).join(", ")}
          onChange={(e) => sec("digest", "weekly_report_times",
            e.target.value.split(",").map((x) => x.trim()).filter(Boolean))}
          data-testid="weekly-times-input" />
      </F>
      <F label="Notas a listar por medio">
        <Input type="number" min="1" max="30" value={s.digest?.weekly_report_top ?? 8}
          onChange={(e) => sec("digest", "weekly_report_top", Number(e.target.value))}
          data-testid="weekly-top-input" />
      </F>
      <ChannelSwitches s={s} sec={sec} field="weekly_report_channels" testidPrefix="weekly" />
      <div className="flex items-center gap-3">
        <Switch checked={s.digest?.weekly_report_enabled !== false}
          onCheckedChange={(v) => sec("digest", "weekly_report_enabled", v)}
          data-testid="weekly-enabled-switch" />
        <span className="text-sm">Parte semanal habilitado</span>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <Button variant="outline" onClick={previewWeekly}
          disabled={busy === "weekly-preview"} data-testid="weekly-preview-btn">
          <Play className="h-4 w-4 mr-2" />
          {busy === "weekly-preview" ? "Armando…" : "Ver borrador"}
        </Button>
        <Button variant="outline" onClick={sendWeeklyNow} disabled={busy === "weekly"}
          data-testid="run-weekly-now-btn">
          <Play className="h-4 w-4 mr-2" />
          {busy === "weekly" ? "Enviando…" : "Enviar parte semanal ahora"}
        </Button>
      </div>
      {weeklyPreview && (
        <pre className="mt-3 text-xs whitespace-pre-wrap bg-muted/50 border border-border p-3 max-h-64 overflow-y-auto"
             data-testid="weekly-preview-text">{weeklyPreview}</pre>
      )}
      {weekly && (
        <p className="text-xs text-muted-foreground mt-2" data-testid="weekly-result">
          {weekly.ran
            ? `${weekly.range} · enviado por ${(weekly.channels || []).join(", ") || "ningún canal"} · ${weekly.counts?.publicadas ?? 0} publicadas · líder: ${weekly.leader?.outlet || "—"}`
            : `Sin enviar: ${weekly.reason}`}
          {(weekly.errors || []).length > 0 && ` · ${weekly.errors[0].error}`}
        </p>
      )}
    </div>
  </>
);
